import { useState, useEffect } from 'react';
import { Checkbox } from '@components/checkbox/Checkbox';
import { Dialog } from '@components/dialog';
import { Select } from '@components/select/Select';
import { DatePicker } from '@components/datePicker/DatePicker';
import { Box, Grid, Theme, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ToastType } from '@components/toast/ToastType.enum';
import {
  AvailableBrokerageOfferingSearchResponse,
  OfferingWritingCompanySearchResponse,
  ProductOptionSearchResponse,
} from '@lio/api-types';
import { useCopyQuote, useQuote } from '@services/quotes';
import { useStore } from '@store/GlobalStore';
import { useProductOfferings } from '@services/productOffering';
import { uniqBy } from 'lodash';
import { getEarliestEffectiveOn, getLatestEffectiveThrough } from '@utils/brokerages';
import { addDays } from 'date-fns';
import { AvailableBrokerageOfferingsItem, OfferingWritingCompanyItem } from '@models/productOffering';
import { IProductLabel, useProductLabels } from '@services/productLabel';
import { useProductOptions } from '@services/productOption';
import { useWritingCompany } from '@services/writingCompany';
import { setDateToMidnight, fakeLocalMidnightFromTZMidnight } from '@utils/date';
import { Tooltip } from '@components/tooltip/Tooltip';
import { IconButton } from '@components/iconButton';
import { InfoCircle } from '@assets/svgs';
import Loading from '@components/Loading';
import { ArrayItem } from '@utils/commonTypes';
import { shallow } from 'zustand/shallow';
import { Button } from '@components/button';
import { getTypographyVariants } from '@theme/styleOverrides';

interface Props {
  quoteId: string;
  handleClose: () => void;
}

interface Values {
  effectiveOn?: Date | null;
  offering?: Partial<ArrayItem<AvailableBrokerageOfferingSearchResponse['items']>>;
  productLabel?: Partial<IProductLabel>;
  productOption?: Partial<ArrayItem<ProductOptionSearchResponse['items']>>;
  writingCompany?: Partial<ArrayItem<OfferingWritingCompanySearchResponse['items']>>;
  includeArtifacts?: boolean;
  enableV2Forms?: boolean;
  migrateV1Forms?: boolean;
}

export const CopyQuoteDialog = ({ quoteId, handleClose }: Props) => {
  const { quote, isLoading: isQuoteLoading } = useQuote(quoteId);
  const { enqueueSnackbar } = useSnackbar();
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);
  const { productOfferings } = useProductOfferings({ brokerageId: quote?.brokerage?.id, includeInherited: true });
  const { isPending, copyQuoteAsync } = useCopyQuote();
  const showFormsV2Toggle = false;
  const [data, setData] = useState<Values>();

  const { writingCompanies, isLoading: loadingWritingCompanies } = useWritingCompany(
    isInternalUser,
    data?.offering?.id ?? '',
  );
  const { productOptions, isLoading: loadingProductOptions } = useProductOptions(
    data?.offering?.insuranceProduct?.id ?? '',
  );
  const { productLabels, isLoading: loadingProductLabels } = useProductLabels(
    data?.offering?.insuranceProduct?.id ?? '',
  );

  useEffect(() => {
    if (quote) {
      setData({
        effectiveOn: null,
        offering: quote.offering,
        productLabel: quote.productLabel ?? undefined,
        writingCompany: quote.writingCompany,
        enableV2Forms: !quote.formsV2Enabled,
        migrateV1Forms: !quote.formsV2Enabled,
      });
    }
  }, [quote]);

  const uniqueOfferings = productOfferings?.items.length
    ? uniqBy(productOfferings.items, (brokerageOffering) => brokerageOffering.id).map((p) => ({
        ...p,
        brokerageOfferingAvailability: {
          jurisdictions: p.brokerageOfferingAvailability.jurisdictions.map((j) => ({
            ...j,
            effectiveOn: new Date(j.effectiveOn),
            effectiveThrough: new Date(j.effectiveThrough),
          })),
        },
      }))
    : [];

  const effectiveProducts = uniqueOfferings.filter((item: AvailableBrokerageOfferingsItem) => {
    const effectiveOn = getEarliestEffectiveOn([...item.brokerageOfferingAvailability.jurisdictions]);
    const effectiveThrough = getLatestEffectiveThrough([...item.brokerageOfferingAvailability.jurisdictions]);
    if (effectiveThrough) {
      return effectiveOn <= addDays(new Date(), 120) && effectiveThrough > new Date(Date.now());
    }
    return effectiveOn <= addDays(new Date(), 120);
  });

  const datePickerParams = () => {
    const productId = data?.offering?.id;
    const effectiveProduct = uniqueOfferings?.find((item: AvailableBrokerageOfferingsItem) => item.id === productId);
    const effectiveOn = effectiveProduct
      ? getEarliestEffectiveOn([...effectiveProduct.brokerageOfferingAvailability.jurisdictions])
      : new Date();
    const effectiveThroughData = effectiveProduct
      ? getLatestEffectiveThrough([...effectiveProduct.brokerageOfferingAvailability.jurisdictions])
      : null;
    const effectiveThrough = effectiveThroughData || addDays(new Date(), 120);
    return {
      startDate: isInternalUser ? effectiveOn : new Date(Math.max(new Date().valueOf(), effectiveOn.valueOf())),
      endDate: new Date(Math.min(addDays(new Date(), 120).valueOf(), effectiveThrough.valueOf())),
    };
  };

  const isEffectiveDateDisabled = (): boolean => {
    if (data?.offering) {
      return !!((uniqueOfferings.length && !data?.offering) || (productOptions?.items.length && !data?.productOption));
    }
    return true;
  };

  const isSubmitDisabled =
    data?.effectiveOn === null ||
    data?.offering === null ||
    data?.writingCompany === null ||
    (productLabels.length > 0 && !data?.productLabel) ||
    (productOptions?.items.length > 0 && !data?.productOption);

  const isDateRangeRestricted = () =>
    new Date(datePickerParams().endDate) < addDays(new Date(), 120) ||
    new Date(datePickerParams().startDate) > new Date();

  const isLoading = loadingWritingCompanies || loadingProductOptions || loadingProductLabels;

  const handleSubmit = async () => {
    if (quote && data && data.offering && data.effectiveOn) {
      const copyData = {
        sourceQuoteId: quote.id,
        effectiveOn: setDateToMidnight(data.effectiveOn),
        writingCompanyId: isInternalUser && data.writingCompany ? data.writingCompany.id : undefined,
        offeringId: isInternalUser && data.offering.id ? data.offering.id : undefined,
        productOptionId: data.productOption ? data.productOption?.id : undefined,
        productLabelId: data.productOption ? undefined : data.productLabel ? data.productLabel.id : undefined,
        includeArtifacts: isInternalUser && data.includeArtifacts ? data.includeArtifacts : false,
        enableV2Forms: !!data.enableV2Forms,
        migrateV1Forms: !!data.migrateV1Forms,
      };

      copyQuoteAsync(copyData, {
        onSuccess: async (response: any) => {
          const data = await response.json();

          enqueueSnackbar('Quote copied successfully', {
            variant: ToastType.LinkToast,
            persist: true,
            link: (
              <Button
                href={`/quote/${data?.id}`}
                variant='text'
                sx={{ fontSize: (theme: Theme) => getTypographyVariants(theme.palette.fontSize).bodySmall.fontSize }}
              >
                View Quote
              </Button>
            ),
          });
          handleClose();
        },
        onError: async (error: any) => {
          const data = await error?.response?.json();

          enqueueSnackbar(data.error.message ?? 'Failed to copy quote', {
            variant: ToastType.Error,
            persist: true,
          });
          handleClose();
        },
      });
    }
  };

  return isQuoteLoading || !quote ? (
    <Box
      data-testid='content-loading-container'
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '345px',
      }}
    >
      <Loading />
    </Box>
  ) : (
    <Dialog
      open={Boolean(quote)}
      title={`Copy Quote #${quote?.policyNumber?.policyNumber}`}
      primaryBtnAction={handleSubmit}
      primaryBtnText='Copy Quote'
      isPrimaryBtnLoading={isPending}
      isPrimaryBtnDisabled={isSubmitDisabled}
      secondaryBtnAction={handleClose}
      secondaryBtnText='Exit'
      handleCloseDialog={handleClose}
    >
      {isLoading ? (
        <Box
          data-testid='content-loading-container'
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '345px',
          }}
        >
          <Loading />
        </Box>
      ) : (
        <Grid
          size={{ xs: 11 }}
          sx={{
            maxWidth: 'none !important',
            width: '100%',
            padding: '26px',
            '.MuiFormControl-root': {
              width: '100%',
            },
          }}
        >
          <Select
            id='Product'
            value={data?.offering?.id}
            inputLabel='Product'
            readOnly={!isInternalUser}
            variant='outlined'
            color='secondary'
            onChange={(e) => {
              const selectedValue = effectiveProducts?.find(
                (p: AvailableBrokerageOfferingsItem) => p.id === e.target.value,
              );
              if (!selectedValue) return;
              setData((prev) => ({ ...prev, offering: selectedValue }));
            }}
            fullWidth
            items={effectiveProducts
              .sort((a, b) => (a.label ?? a.insuranceProduct.name).localeCompare(b.label ?? b.insuranceProduct.name))
              .map((item: AvailableBrokerageOfferingsItem) => {
                return {
                  key: item.id,
                  value: item.id,
                  label: item.label !== null ? item.label : item.insuranceProduct.name,
                };
              })}
            sx={{ marginBottom: '24px' }}
          />

          {isInternalUser && productLabels.length > 0 && (
            <Select
              id='Product Label'
              value={data?.productLabel ? data?.productLabel.id : ''}
              inputLabel='Product Label'
              variant='outlined'
              color='secondary'
              onChange={(e) => {
                const selectedValue = productLabels?.find((pl) => pl.id === e.target.value);
                if (!selectedValue) return;
                setData((prev) => ({ ...prev, productLabel: selectedValue }));
              }}
              fullWidth
              items={productLabels?.map((item) => ({
                key: item.id,
                value: item.id,
                label: item.name,
                ...item,
              }))}
              sx={{ marginBottom: '24px' }}
            />
          )}

          {productOptions?.items.length > 0 && (
            <Select
              id='Business Class'
              value={data?.productOption ? data?.productOption.id : ''}
              inputLabel='Business Class'
              variant='outlined'
              color='secondary'
              defaultValue=''
              onChange={(e) => {
                const selectedValue = productOptions?.items.find((po) => po.id === e.target.value);
                setData((prev) => ({ ...prev, productOption: selectedValue }));
              }}
              fullWidth
              items={productOptions?.items.map((item) => ({
                key: item.id,
                value: item.id,
                label: item.name,
                ...item,
              }))}
              sx={{ marginBottom: '24px' }}
            />
          )}

          {isInternalUser && data?.offering && data?.writingCompany && (
            <Select
              id='Writing Company'
              value={data?.writingCompany.id}
              inputLabel='Writing Company'
              variant='outlined'
              color='secondary'
              defaultValue=''
              onChange={(e) => {
                const selectedValue = writingCompanies?.items?.find(
                  (p: OfferingWritingCompanyItem) => p?.writingCompany.id === e.target.value,
                );
                setData((prev) => ({ ...prev, writingCompany: selectedValue?.writingCompany }));
              }}
              fullWidth
              items={writingCompanies?.items?.map((item: OfferingWritingCompanyItem) => ({
                key: item.id,
                value: item.writingCompany.id,
                label: item.writingCompany.name,
                ...item,
              }))}
              sx={{ marginBottom: '24px' }}
            />
          )}

          <DatePicker
            onChange={(date) => {
              setData((prev) => ({ ...prev, effectiveOn: date }));
            }}
            value={data?.effectiveOn ? new Date(data?.effectiveOn) : null}
            data-testid='New-Policy-Effective-Date'
            disabled={isEffectiveDateDisabled()}
            minDate={fakeLocalMidnightFromTZMidnight(datePickerParams().startDate)}
            maxDate={fakeLocalMidnightFromTZMidnight(datePickerParams().endDate)}
            textFieldProps={{
              inputLabel: 'New Policy Effective Date',
            }}
          />
          {!isEffectiveDateDisabled() && isDateRangeRestricted() && (
            <Tooltip
              sx={{ marginRight: '-36px' }}
              placement='left-start'
              title='Date range is restricted due to product availability'
            >
              <IconButton variant='circularDark'>
                <InfoCircle />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '20px' }}>
            {isInternalUser && (
              <Checkbox
                sx={{ marginLeft: -1, marginTop: '-22px' }}
                checked={!!data?.includeArtifacts}
                inputProps={
                  { 'aria-labelledby': 'include-artifacts-label' } as React.InputHTMLAttributes<HTMLInputElement>
                }
                onChange={(event) => {
                  setData((prev) => ({ ...prev, includeArtifacts: event.target.checked }));
                }}
              />
            )}

            <Grid
              sx={{
                flexDirection: 'row',
                alignItems: 'center',
                pr: '50px',
              }}
            >
              <Typography
                id='include-artifacts-label'
                variant='bodySmall'
                css
                sx={{
                  lineHeight: '20px',
                  display: 'block',
                }}
              >
                Include Attachments, Notes and Messages
              </Typography>
              <Typography variant='caption' sx={{ lineHeight: '20px', fontWeight: 'standard' }}>
                Do not select if you plan to change the brokerage on the copied quote.
              </Typography>
            </Grid>
          </Box>
          {showFormsV2Toggle && (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '20px' }}>
              <Checkbox
                sx={{ marginLeft: -1, marginTop: '-22px' }}
                checked={!!data?.enableV2Forms}
                inputProps={
                  { 'aria-labelledby': 'enable-v2-forms-label' } as React.InputHTMLAttributes<HTMLInputElement>
                }
                onChange={(event) => {
                  setData((prev) => ({
                    ...prev,
                    enableV2Forms: event.target.checked,
                    migrateV1Forms: event.target.checked ? !!prev?.migrateV1Forms : false,
                  }));
                }}
              />
              <Grid
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  pr: '50px',
                }}
              >
                <Typography
                  id='enable-v2-forms-label'
                  variant='bodySmall'
                  sx={{
                    lineHeight: '20px',
                    display: 'block',
                  }}
                >
                  Enable V2 Forms
                </Typography>
                <Typography variant='caption' sx={{ lineHeight: '20px', fontWeight: 'standard' }}>
                  Enable FormsV2 on the copied quote
                </Typography>
              </Grid>
            </Box>
          )}
          {showFormsV2Toggle && (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '20px' }}>
              <Checkbox
                sx={{ marginLeft: -1, marginTop: '-22px' }}
                checked={!!data?.migrateV1Forms}
                disabled={!data?.enableV2Forms}
                inputProps={
                  { 'aria-labelledby': 'migrate-v1-forms-label' } as React.InputHTMLAttributes<HTMLInputElement>
                }
                onChange={(event) => {
                  setData((prev) => ({ ...prev, migrateV1Forms: event.target.checked }));
                }}
              />
              <Grid
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  pr: '50px',
                }}
              >
                <Typography
                  id='migrate-v1-forms-label'
                  variant='bodySmall'
                  sx={{
                    lineHeight: '20px',
                    display: 'block',
                  }}
                >
                  Migrate V1 Form Data
                </Typography>
                <Typography variant='caption' sx={{ lineHeight: '20px', fontWeight: 'standard' }}>
                  Migrate V1 form data to V2 format (requires rated source quote)
                </Typography>
              </Grid>
            </Box>
          )}
        </Grid>
      )}
    </Dialog>
  );
};
